import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Mic, Send, Loader2 } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { Header } from "@/components/Header";
import { SimpleRecorder } from "@/components/SimpleRecorder";
import { ShareButton } from "@/components/ShareButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VoiceStudio() {
  const { t, fontClass } = useLang();
  const [kind, setKind] = useState("message");
  const [form, setForm] = useState({ from_name: "", to_name: "", message: "" });
  const [blob, setBlob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const kinds = [
    { key: "message", label: t("voice_kind_message") },
    { key: "blessing", label: t("voice_kind_blessing") },
    { key: "story", label: t("voice_kind_story") },
  ];

  const send = async () => {
    if (!form.from_name.trim()) { toast.error(t("voice_from")); return; }
    if (!blob) { toast.error(t("voice_record_hint")); return; }
    const fd = new FormData();
    fd.append("from_name", form.from_name);
    fd.append("to_name", form.to_name);
    fd.append("message", form.message);
    fd.append("kind", kind);
    fd.append("audio", blob, "voice.webm");
    setSaving(true);
    try {
      const r = await axios.post(`${API}/voice-messages`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCreated(r.data.id);
    } catch (e) { toast.error("Could not send. Please try again."); }
    finally { setSaving(false); }
  };

  const shareUrl = created ? `${window.location.origin}/voice/${created}` : "";

  return (
    <div className="min-h-screen pb-24" style={{ background: "var(--bg)" }}>
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6">
        <Link to="/rooms/sindu-kamare" data-testid="voice-back-link" className="inline-flex items-center gap-2 font-ui font-bold text-sm" style={{ color: "var(--accent)" }}>
          <ArrowLeft size={18} /> {t("back_room")}
        </Link>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6">
        <h1 className={`${fontClass()} text-2xl sm:text-3xl font-bold flex items-center gap-2`} style={{ color: "var(--text)" }}>
          <Mic size={26} style={{ color: "var(--accent)" }} /> {t("voice_title")}
        </h1>
        <p className="font-ui text-base mt-2" style={{ color: "var(--text-2)" }}>{t("voice_sub")}</p>

        {!created ? (
          <div className="mt-6 rounded-3xl p-5 sm:p-6 space-y-4" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
            <div className="flex gap-2">
              {kinds.map((k) => (
                <button key={k.key} onClick={() => setKind(k.key)} data-testid={`voice-kind-${k.key}`} className="flex-1 h-11 rounded-full font-ui font-bold text-sm" style={{ background: kind === k.key ? "var(--accent)" : "var(--bg)", color: kind === k.key ? "#fff" : "var(--text-2)", border: "1px solid var(--border)" }}>{k.label}</button>
              ))}
            </div>
            <input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} placeholder={t("voice_from")} data-testid="voice-from" className="w-full h-13 px-4 py-3 rounded-xl font-ui outline-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />
            <input value={form.to_name} onChange={(e) => setForm({ ...form, to_name: e.target.value })} placeholder={t("voice_to")} data-testid="voice-to" className="w-full h-13 px-4 py-3 rounded-xl font-ui outline-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />
            <input value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t("voice_msg")} data-testid="voice-message" className="w-full h-13 px-4 py-3 rounded-xl font-ui outline-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />
            <SimpleRecorder onRecorded={setBlob} />
            <button onClick={send} disabled={saving} data-testid="voice-send" className="w-full h-13 py-3 rounded-xl font-ui font-bold text-base flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--player-bg)", color: "var(--player-accent)" }}>
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} {t("voice_send")}
            </button>
          </div>
        ) : (
          <div className="mt-6 rounded-3xl p-6 text-center space-y-4" style={{ background: "var(--card)", border: "2px solid var(--accent-glow)" }} data-testid="voice-created">
            <p className={`${fontClass()} text-xl font-bold`} style={{ color: "var(--text)" }}>🎙️ {t("voice_created")}</p>
            <audio controls src={`${API}/voice-messages/${created}`.replace("/voice-messages/", "/media?token=")} className="hidden" />
            <div className="flex justify-center">
              <ShareButton testid="voice-share" text={`🎙️ ${form.from_name} sent a voice ${kind} ${form.to_name ? "for " + form.to_name : ""}`.trim()} url={shareUrl} />
            </div>
            <Link to={`/voice/${created}`} data-testid="voice-open" className="inline-block font-ui font-bold text-sm" style={{ color: "var(--accent)" }}>{t("open")} →</Link>
          </div>
        )}
      </div>
    </div>
  );
}
